import type { ProjectItem } from '@customink/project-service-react-components';
import { useWCIBMO } from '@customink/project-service-react-components';
import { useAtomValue } from 'jotai';
import { useEffect, useRef } from 'react';

import { validateEditGroupSizes } from '../../validators';

import type { ModalSource } from '@/atoms/editSizesModalAtoms';
import { modalStateAtom } from '@/atoms/editSizesModalAtoms';
import { googleAnalyticsTag } from '@/components/vendors/googleAnalytics';

const SOURCE_MAP: Record<ModalSource, string> = {
  initial_load: 'initial load',
  alert_link: 'alert link',
  edit_sizes_link: 'edit sizes link',
};

type UseEditSizesTagsProps = {
  totalQuantity: number;
  minQuantity: number;
  designCid: string;
  projectItems: ProjectItem[];
};

type QuantityParams = {
  edit_sizes_modal_total_quantity: number;
  edit_sizes_modal_minimum_needed?: number;
};

function buildQuantityParams(
  totalQuantity: number,
  minQuantity: number,
): QuantityParams {
  if (totalQuantity >= minQuantity) {
    return {
      edit_sizes_modal_total_quantity: totalQuantity,
    };
  }

  return {
    edit_sizes_modal_total_quantity: totalQuantity,
    edit_sizes_modal_minimum_needed: minQuantity,
  };
}

/**
 * Hook for firing analytics tags for the edit sizes modal
 * NOTE: This hook also fires the modal launch tag on mount in useEffect
 */
export default function useEditSizesTags({
  designCid,
  totalQuantity,
  minQuantity,
  projectItems,
}: UseEditSizesTagsProps) {
  const modalState = useAtomValue(modalStateAtom);
  const { wcibmo, isLoading: isLoadingWCIBMO } = useWCIBMO(designCid);
  // Enter quantities WCIBMO is calculated on frontend and not from the quote service API
  const formValidationError = validateEditGroupSizes({
    projectItems,
  });
  const initialModalLoad = useRef(false);

  const fireModalLaunchTag = (wcibmo?: string | null) => {
    const source = modalState?.source;
    if (!source) {
      return;
    }

    googleAnalyticsTag({
      event: 'edit_sizes_modal',
      edit_sizes_modal_interaction: 'launch',
      edit_sizes_modal_source: SOURCE_MAP[source],
      ...(wcibmo ? { edit_sizes_modal_alert: wcibmo } : {}),
      ...buildQuantityParams(totalQuantity, minQuantity),
    });
  };

  const fireModalCloseTag = () => {
    googleAnalyticsTag({
      event: 'edit_sizes_modal',
      edit_sizes_modal_interaction: 'close',
      ...buildQuantityParams(totalQuantity, minQuantity),
    });
  };

  const fireModalUpdateTag = () => {
    googleAnalyticsTag({
      event: 'edit_sizes_modal',
      edit_sizes_modal_interaction: 'update',
      ...buildQuantityParams(totalQuantity, minQuantity),
    });
  };

  const fireProjectItemDeleteTag = () => {
    googleAnalyticsTag({
      event: 'edit_sizes_modal',
      edit_sizes_modal_interaction: 'remove',
      ...buildQuantityParams(totalQuantity, minQuantity),
    });
  };

  // Fire tag on mount for the modal launch event
  useEffect(() => {
    // Skip if already fired tag
    if (initialModalLoad.current) {
      return;
    }

    // Skip if we have items with quantity but no WCIBMO data yet
    if (!formValidationError && isLoadingWCIBMO) {
      return;
    }

    fireModalLaunchTag(wcibmo || formValidationError);
    initialModalLoad.current = true;
  }, [isLoadingWCIBMO, wcibmo, fireModalLaunchTag, formValidationError]);

  return {
    fireModalLaunchTag,
    fireModalCloseTag,
    fireModalUpdateTag,
    fireProjectItemDeleteTag,
  };
}
